import { z } from "zod";
import type { TenantLicensePlan } from "./common.js";
import type { UsageQuotaView } from "./usage.js";

export const billingProviderSchema = z.enum(["simulated", "alipay"]);
export const billingCurrencySchema = z.enum(["TWD"]);
export const billingPeriodSchema = z.enum(["monthly", "quarterly", "yearly"]);
export const billingPlanIdSchema = z.enum([
  "shudian_monthly",
  "shudian_quarterly",
  "shudian_yearly"
]);
export const billingOrderStatusSchema = z.enum([
  "pending",
  "paid",
  "cancelled",
  "expired",
  "failed",
  "refunded"
]);
export const billingSubscriptionStatusSchema = z.enum([
  "active",
  "past_due",
  "cancelled",
  "expired"
]);
export const billingWebhookEventStatusSchema = z.enum([
  "received",
  "processed",
  "failed"
]);

export const billingCheckoutSessionCreateSchema = z.object({
  planId: billingPlanIdSchema,
  successUrl: z.string().trim().url().optional(),
  cancelUrl: z.string().trim().url().optional(),
  mode: z.enum(["page_redirect", "qr_code"]).optional()
});

export const billingOrderParamsSchema = z.object({
  orderId: z.string().trim().min(1).max(160)
});

export const simulatedBillingWebhookSchema = z.object({
  eventId: z.string().trim().min(1).max(160),
  eventType: z.enum(["checkout.session.completed", "payment.failed", "refund.succeeded"]),
  orderId: z.string().trim().min(1).max(160),
  providerTransactionId: z.string().trim().min(1).max(160).optional(),
  occurredAt: z.string().trim().min(1).optional()
});

export const billingSettingsProviderSchema = z.enum(["disabled", "simulated", "alipay"]);

const billingPlanQuotaLimitSchema = z.object({
  usageType: z.string().trim().min(1).max(80),
  limit: z.number().int().positive().max(1_000_000_000)
});

export const adminBillingPlanSettingsSchema = z.object({
  planId: billingPlanIdSchema,
  name: z.string().trim().min(1).max(80),
  description: z.string().trim().min(1).max(160),
  priceAmount: z.number().int().min(0).max(100_000_000),
  quotaLimits: z.array(billingPlanQuotaLimitSchema).max(10),
  entitlements: z.array(z.string().trim().min(1).max(120)).min(1).max(100),
  deviceLimit: z.number().int().min(1).max(50),
  storeLimit: z.number().int().min(0).max(10000).default(50),
  active: z.boolean(),
  features: z.array(z.string().trim().min(1).max(80)).min(1).max(8),
  renewalHint: z.string().trim().min(1).max(80),
  sortOrder: z.number().int().min(0).max(100)
}).superRefine((value, ctx) => {
  const expectedPeriod: Record<BillingPlanId, BillingPeriod> = {
    shudian_monthly: "monthly",
    shudian_quarterly: "quarterly",
    shudian_yearly: "yearly"
  };
  if (!expectedPeriod[value.planId]) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: ["planId"],
      message: "套餐 ID 不合法"
    });
  }
  if (new Set(value.quotaLimits.map((item) => item.usageType)).size !== value.quotaLimits.length) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: ["quotaLimits"],
      message: "套餐额度类型不能重复"
    });
  }
});

export type AdminBillingPlanSettingsInput = z.infer<typeof adminBillingPlanSettingsSchema>;

export interface BillingPlanSnapshot {
  planId: BillingPlanId;
  name: string;
  billingPeriod: BillingPeriod;
  licensePlan: TenantLicensePlan;
  priceAmount: number;
  currency: BillingCurrency;
  quotaLimits: UsageQuotaView[];
  entitlements: string[];
  deviceLimit: number;
  storeLimit: number;
  description: string;
  features: string[];
  renewalHint: string;
  sortOrder: number;
}

export const adminBillingSettingsSchema = z.object({
  provider: billingSettingsProviderSchema,
  enabled: z.boolean(),
  plans: z.array(adminBillingPlanSettingsSchema).max(3).optional(),
  alipay: z
    .object({
      appId: z.string().trim().min(1).max(64),
      gatewayUrl: z.string().trim().url(),
      notifyUrl: z.string().trim().url(),
      returnUrl: z.string().trim().url().optional(),
      appCertSn: z.string().trim().min(1).max(128).optional(),
      alipayRootCertSn: z.string().trim().min(1).max(256).optional()
    })
    .optional()
}).superRefine((value, ctx) => {
  if (value.provider === "alipay" && value.enabled && !value.alipay) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: ["alipay"],
      message: "启用支付宝时必须填写支付宝接入参数"
    });
  }
  if (value.plans) {
    const planIds = value.plans.map((plan) => plan.planId);
    if (new Set(planIds).size !== planIds.length) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["plans"],
        message: "套餐 ID 不能重复"
      });
    }
  }
});

export type BillingProvider = z.infer<typeof billingProviderSchema>;
export type BillingSettingsProvider = z.infer<typeof billingSettingsProviderSchema>;
export type BillingCurrency = z.infer<typeof billingCurrencySchema>;
export type BillingPeriod = z.infer<typeof billingPeriodSchema>;
export type BillingPlanId = z.infer<typeof billingPlanIdSchema>;
export type BillingOrderStatus = z.infer<typeof billingOrderStatusSchema>;
export type BillingSubscriptionStatus = z.infer<typeof billingSubscriptionStatusSchema>;
export type BillingWebhookEventStatus = z.infer<typeof billingWebhookEventStatusSchema>;
export type BillingCheckoutSessionCreateInput = z.infer<typeof billingCheckoutSessionCreateSchema>;
export type BillingOrderParams = z.infer<typeof billingOrderParamsSchema>;
export type SimulatedBillingWebhookInput = z.infer<typeof simulatedBillingWebhookSchema>;
export type AdminBillingSettingsInput = z.infer<typeof adminBillingSettingsSchema>;

export interface AlipayBillingSettingsView {
  appId: string;
  gatewayUrl: string;
  notifyUrl: string;
  returnUrl?: string;
  appCertSn?: string;
  alipayRootCertSn?: string;
  appPrivateKeyConfigured: boolean;
  alipayPublicKeyConfigured: boolean;
  webhookSecretConfigured: boolean;
}

export interface AlipayBillingSecretStatusView {
  appPrivateKeyConfigured: boolean;
  alipayPublicKeyConfigured: boolean;
  webhookSecretConfigured: boolean;
}

export interface AdminBillingSettingsView {
  provider: BillingSettingsProvider;
  enabled: boolean;
  plans: BillingPlanCatalogItem[];
  alipay?: AlipayBillingSettingsView;
  alipaySecretStatus: AlipayBillingSecretStatusView;
  updatedAt: string;
}

export interface BillingPlanCatalogItem {
  planId: BillingPlanId;
  name: string;
  billingPeriod: BillingPeriod;
  licensePlan: TenantLicensePlan;
  priceAmount: number;
  currency: BillingCurrency;
  quotaLimits: UsageQuotaView[];
  entitlements: string[];
  deviceLimit: number;
  storeLimit: number;
  active: boolean;
  description: string;
  features: string[];
  renewalHint: string;
  sortOrder: number;
}

export interface BillingOrderView {
  id: string;
  tenantId: string;
  userId: string;
  planId: BillingPlanId;
  billingPeriod: BillingPeriod;
  licensePlan: TenantLicensePlan;
  amount: number;
  currency: BillingCurrency;
  status: BillingOrderStatus;
  provider: BillingProvider;
  providerOrderId: string;
  providerTransactionId?: string;
  checkoutUrl: string;
  planSnapshot?: BillingPlanSnapshot;
  successUrl?: string;
  cancelUrl?: string;
  paidAt?: string;
  expiresAt: string;
  createdAt: string;
  updatedAt: string;
}

export interface BillingSubscriptionView {
  id: string;
  tenantId: string;
  planId: BillingPlanId;
  billingPeriod: BillingPeriod;
  licensePlan: TenantLicensePlan;
  status: BillingSubscriptionStatus;
  provider: BillingProvider;
  providerSubscriptionId?: string;
  currentPeriodStart: string;
  currentPeriodEnd: string;
  createdAt: string;
  updatedAt: string;
}

export interface BillingWebhookEventView {
  id: string;
  provider: BillingProvider;
  eventId: string;
  eventType: string;
  status: BillingWebhookEventStatus;
  tenantId?: string;
  orderId?: string;
  errorCode?: string;
  processedAt?: string;
  createdAt: string;
}

export interface BillingPlansResponse {
  plans: BillingPlanCatalogItem[];
}

export interface BillingCheckoutSessionResponse {
  order: BillingOrderView;
  checkoutUrl: string;
  payment?: BillingPaymentSessionView;
}

export interface BillingPaymentSessionView {
  provider: BillingProvider;
  mode: "page_redirect" | "qr_code";
  checkoutUrl?: string;
  qrCodeUrl?: string;
  expiresAt: string;
}

export interface BillingOrderResponse {
  order: BillingOrderView;
}

export interface BillingOrderListResponse {
  orders: BillingOrderView[];
}

export interface BillingSubscriptionResponse {
  subscription: BillingSubscriptionView | null;
}

export interface BillingWebhookResponse {
  ok: true;
  duplicate: boolean;
  event: BillingWebhookEventView;
  order?: BillingOrderView;
  subscription?: BillingSubscriptionView;
}

export const billingEventTypeSchema = z.enum([
  "license_created",
  "license_updated",
  "license_renewed",
  "license_plan_changed",
  "license_status_changed"
]);

export type BillingEventType = z.infer<typeof billingEventTypeSchema>;

export interface BillingEventView {
  id: string;
  tenantId: string;
  eventType: BillingEventType;
  plan: "monthly" | "quarterly" | "yearly";
  status: "active" | "disabled" | "expired";
  deviceLimit: number;
  storeLimit: number;
  expiresAt: string;
  entitlements: string[];
  metadata: Record<string, unknown>;
  createdByAdminId: string | null;
  createdAt: string;
}

export interface AdminBillingEventListResponse {
  events: BillingEventView[];
}
